import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { MEDIA, CONTENT_FORMATS, CASE_STUDIES } from "@/data/content";

export function Showreel() {
  return (
    <section data-testid="showreel-section" className="relative py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Showreel</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
              Il tuo lavoro merita di essere raccontato bene.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Ogni attività ha una storia, un'identità e dei punti di forza. Il nostro compito è
              trasformarli in contenuti che attirino l'attenzione e facciano ricordare il brand.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 aspect-video"
          >
            <video
              data-testid="showreel-video"
              className="w-full h-full object-cover"
              autoPlay muted loop playsInline
              poster={MEDIA.heroFallback}
            >
              <source src={MEDIA.heroVideo} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-[#050505]/20 pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function ContentFormats() {
  return (
    <section data-testid="formats-section" className="relative py-24 lg:py-32 bg-anthracite/40 border-y border-white/5">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Format e contenuti</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Non facciamo dodici volte lo stesso video
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Una comunicazione efficace ha bisogno di varietà. Per questo alterniamo contenuti
            differenti, mantenendo sempre riconoscibile lo stile dell'attività.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          {CONTENT_FORMATS.map((f, i) => (
            <motion.span
              key={f}
              data-testid={`format-tag-${i}`}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03 }}
              className="rounded-full border border-white/12 bg-[#0b0b0b] px-5 py-2.5 text-sm text-slate-200 hover:border-teal-light/40 hover:text-teal-light transition-colors"
            >
              {f}
            </motion.span>
          ))}
        </div>

        <p className="mt-9 text-lg text-white max-w-3xl">
          Ogni contenuto deve avere un obiettivo preciso:{" "}
          <span className="text-teal-light">attirare, incuriosire, spiegare, intrattenere o vendere.</span>
        </p>
      </div>
    </section>
  );
}

export function Sponsorships() {
  return (
    <section data-testid="sponsorships-section" className="relative py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Sponsorizzazioni incluse</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
              Non pubblichiamo e speriamo che qualcuno guardi
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Ogni mese il 10% del valore del piano viene già destinato alla promozione dei
              contenuti. Questo permette di mostrare una parte dei video anche a persone che ancora
              non conoscono l'attività, aumentando la diffusione della comunicazione.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {["Metodo", "Maggiore distribuzione", "Continuità", "Test dei contenuti", "Nuovo pubblico"].map((t, i) => (
                <span key={i} data-testid={`sponsor-point-${i}`} className="rounded-full border border-teal-light/25 bg-teal/10 px-4 py-1.5 text-sm text-teal-light">{t}</span>
              ))}
            </div>
          </div>

          <div className="grid gap-4">
            {[
              { plan: "Fast 8", amount: "49,90 €" },
              { plan: "Fast 12", amount: "69,90 €" },
            ].map((s, i) => (
              <div key={i} data-testid={`sponsor-card-${i}`} className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-7 flex items-center justify-between">
                <div>
                  <p className="font-display text-xl text-white">{s.plan}</p>
                  <p className="text-sm text-slate-400 mt-1">al mese in sponsorizzazioni</p>
                </div>
                <span className="font-display text-3xl text-teal-light">{s.amount}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function CaseStudies() {
  return (
    <section data-testid="casestudies-section" className="relative py-24 lg:py-32 bg-anthracite/40 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Casi studio</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Non mostriamo soltanto bei video. Mostriamo il lavoro che c'è dietro.
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {CASE_STUDIES.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Film;
            return (
              <motion.div
                key={i}
                data-testid={`casestudy-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-3xl border border-white/10 bg-[#0b0b0b] p-8"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal/15 text-teal-light">
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 font-display text-xl text-white">{c.sector}</h3>
                <ul className="mt-5 space-y-2.5">
                  {c.rows.map((r, ri) => (
                    <li key={ri} className="flex items-center gap-3 text-sm text-slate-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-teal-light shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
        <p className="mt-8 text-xs text-slate-500">Struttura pronta per inserire successivamente i dati reali di ogni progetto.</p>
      </div>
    </section>
  );
}
